import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';

export default function CartPage() {
  const { cart, updateCartItem, removeFromCart, loading } = useContext(CartContext);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-sm p-4 flex gap-4">
                <div className="w-24 h-24 bg-zinc-800 rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-zinc-800 rounded w-1/2" />
                  <div className="h-4 bg-zinc-800 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!cart.items?.length) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="cart-empty">
          <ShoppingBag className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mb-4">
            Your Cart is Empty
          </h1>
          <p className="text-muted-foreground mb-8">
            Add some products to get started
          </p>
          <Link to="/products">
            <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
              Browse Products
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="cart-page">
        <h1 className="text-3xl font-black text-foreground uppercase tracking-tight mb-8">
          Shopping Cart
        </h1>

        <div className="space-y-4">
          {cart.items.map((item) => (
            <div
              key={item.product_id}
              className="bg-card border border-border rounded-sm p-4 flex gap-4"
              data-testid={`cart-item-${item.product_id}`}
            >
              {/* Image */}
              <div className="w-24 h-24 bg-zinc-900 rounded-sm overflow-hidden flex-shrink-0">
                {item.product_image ? (
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600">
                    <ShoppingBag className="h-8 w-8" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <Link to={`/products/${item.sku}`} className="hover:text-[#78BE20]">
                  <h3 className="font-bold text-foreground truncate">{item.product_name}</h3>
                </Link>
                <p className="text-sm text-muted-foreground font-mono">SKU: {item.sku}</p>
                <p className="text-[#78BE20] font-bold font-mono mt-1">
                  ${item.unit_price.toFixed(2)}
                </p>
              </div>

              {/* Quantity */}
              <div className="flex flex-col items-end justify-between">
                <div className="flex items-center border border-border rounded-sm">
                  <button
                    onClick={() => updateCartItem(item.product_id, item.quantity - 1)}
                    className="p-2 hover:bg-zinc-800 transition-colors"
                    disabled={item.quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={item.max_order_qty || item.stock_qty || 999}
                    value={item.quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      const max = item.max_order_qty || item.stock_qty || 999;
                      if (val >= 1) updateCartItem(item.product_id, Math.min(val, max));
                    }}
                    className="w-12 text-center font-mono text-sm bg-transparent border-none outline-none text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    data-testid={`cart-qty-input-${item.product_id}`}
                  />
                  <button
                    onClick={() => updateCartItem(item.product_id, item.quantity + 1)}
                    className="p-2 hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={item.quantity >= (item.max_order_qty || item.stock_qty || 999)}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {item.stock_qty && item.stock_qty < 10 && (
                  <p className="text-xs text-yellow-500 mt-1">{item.stock_qty} in stock</p>
                )}
                <button
                  onClick={() => removeFromCart(item.product_id)}
                  className="text-red-500 hover:text-red-400 p-2"
                  data-testid={`remove-item-${item.product_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Total */}
              <div className="text-right w-24">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="font-bold text-foreground font-mono">
                  ${item.total_price.toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-8 bg-card border border-border rounded-sm p-6">
          <div className="flex justify-between items-center text-lg mb-4">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-bold text-foreground font-mono">
              ${cart.subtotal.toFixed(2)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Shipping calculated at checkout
          </p>
          <Link to="/checkout">
            <Button className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12" data-testid="checkout-btn">
              Proceed to Checkout <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
